import { useLocation, useNavigate } from "react-router-dom";

import { round } from "@repo/system";
import { T, useT } from "@repo/transifex";
import type { StepVariant } from "@repo/types";
import { SwipeToDelete } from "~/components/SwipeToDelete";
import { useCurrentTill } from "~/api/tills/hooks/use-till-current";
import { basketActions } from "~/providers/store/basket";
import { useOrderDiscount } from "~/providers/store/basket/hooks/use-basket";
import type { StepVariantWeight } from "~/providers/store/basket/store.types";
import { type PosProduct } from "~/types/product";
import {
  getBasketDisplayDiscounts,
  shouldShowBasketStrikethrough
} from "~/utils/hybrid-billing-display";
import { formatScaleWeight, getScaleUnitLabel } from "~/utils/weight-units";

type BasketItemProps = {
  basketProductId: string;
  productName: string;
  quantity: number;
  options?: (StepVariant | StepVariantWeight)[];
  totalDisplayPrice: string;
  totalOriginalDisplayPrice: string;
  depositDisplayPrice?: string;
  variantDepositDisplayPrices?: Record<string, string>;
  discountPercentage?: number;
  disabled: boolean;
  stampCardRuleId?: string;
  // hybrid billing
  discount: PosProduct["discount"];
};

type DisplayDiscount = {
  name: string;
  percentage: number;
};

export const BasketItem = ({
  basketProductId,
  productName,
  quantity,
  options,
  totalDisplayPrice,
  totalOriginalDisplayPrice,
  depositDisplayPrice,
  variantDepositDisplayPrices = {},
  discountPercentage,
  disabled,
  stampCardRuleId,
  discount
}: BasketItemProps) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const t = useT();

  const { data: till, status } = useCurrentTill();

  const orderDiscount = useOrderDiscount();

  const hasOrderDiscount = orderDiscount.amount > 0;
  const hasVariants =
    typeof options !== "undefined" && options[0].name !== "scale_weight";

  const weightChoice =
    typeof options !== "undefined" &&
    "weight" in options[0].choices[0] &&
    options[0].choices[0];

  if (status !== "success") return null;

  const displayDiscounts: DisplayDiscount[] = getBasketDisplayDiscounts({
    discount,
    discountPercentage:
      discountPercentage && !hasVariants && !weightChoice
        ? discountPercentage
        : undefined,
    hasOrderDiscount,
    posDiscountLabel: t("discount")
  });

  const shouldDisplayDiscounts = shouldShowBasketStrikethrough({
    discount,
    discountPercentage,
    hasOrderDiscount
  });

  const showPrices = !till.moduleConfig.hidePrices;
  const showVariantDeposits =
    hasVariants &&
    showPrices &&
    Object.keys(variantDepositDisplayPrices).length > 0;

  return (
    <SwipeToDelete
      onDelete={() => {
        basketActions.remove(basketProductId);
      }}
    >
      <button
        className="flex w-full flex-col gap-0.5 border-b border-divider-main bg-background-primary p-4"
        disabled={disabled}
        onClick={() => {
          if (stampCardRuleId) {
            navigate(`${pathname}/stampcard`);
            return;
          }

          navigate(`${pathname}/basket-product/${basketProductId}`);
        }}
        type="button"
      >
        <div className="flex w-full items-center justify-between gap-5">
          <div className="flex items-center gap-5">
            <span className="text-lg font-semibold text-text-primary">
              {quantity}
            </span>

            <div className="flex flex-col gap-0.5 text-left">
              <span className="line-clamp-2 text-lg text-text-primary">
                {productName}
              </span>

              {hasVariants && !showVariantDeposits ? (
                <span className="text-sm text-text-secondary">
                  {options
                    .flatMap((o) =>
                      o.choices.flatMap((c) => {
                        if (c.amount === 0) return [];
                        return `${c.amount}x ${c.name}`;
                      })
                    )
                    .join(", ")}
                </span>
              ) : null}

              {weightChoice !== false && weightChoice.name ? (
                <span className="text-sm text-text-secondary">
                  {`${formatScaleWeight(
                    weightChoice.weight,
                    weightChoice.weightDecimals ?? 0
                  )} ${getScaleUnitLabel(weightChoice.weightUnit ?? "g")}`}
                </span>
              ) : null}

              {shouldDisplayDiscounts
                ? displayDiscounts.map((displayDiscount) => (
                    <BasketItemDiscount
                      key={displayDiscount.name}
                      name={displayDiscount.name}
                      percentage={displayDiscount.percentage}
                    />
                  ))
                : null}
            </div>
          </div>

          {showPrices ? (
            <div className="flex items-center gap-3">
              {shouldDisplayDiscounts ? (
                <span className="text-text-secondary line-through	">
                  {totalOriginalDisplayPrice}
                </span>
              ) : null}

              <span className="font-semibold text-text-secondary">
                {totalDisplayPrice}
              </span>
            </div>
          ) : null}
        </div>

        {showPrices && depositDisplayPrice ? (
          <div className="flex w-full items-baseline justify-between gap-5">
            <div className="flex items-baseline gap-5">
              {/* Mirrors the quantity above so the label lines up with the
                  product name whatever the quantity's width. */}
              <span aria-hidden className="invisible text-lg font-semibold">
                {quantity}
              </span>

              <span className="text-sm text-text-secondary">
                <T _str="+ Deposit Fee" />
              </span>
            </div>

            <span className="text-sm text-text-secondary">
              {depositDisplayPrice}
            </span>
          </div>
        ) : null}

        {showVariantDeposits
          ? options.flatMap((o) =>
              o.choices.flatMap((c) => {
                if (c.amount === 0) return [];
                return (
                  <BasketItemVariant
                    key={c.id}
                    quantity={quantity}
                    label={`${c.amount}x ${c.name}`}
                    depositDisplayPrice={variantDepositDisplayPrices[c.id]}
                  />
                );
              })
            )
          : null}
      </button>
    </SwipeToDelete>
  );
};

type BasketItemDiscountProps = DisplayDiscount;
const BasketItemDiscount = ({ name, percentage }: BasketItemDiscountProps) => {
  return (
    <span className="text-sm text-text-secondary">
      <T
        _str="{percentageDiscount}% {discountName}"
        percentageDiscount={round(percentage, 2)}
        discountName={name}
      />
    </span>
  );
};

type BasketItemVariantProps = {
  quantity: number;
  label: string;
  depositDisplayPrice?: string;
};
const BasketItemVariant = ({
  quantity,
  label,
  depositDisplayPrice
}: BasketItemVariantProps) => {
  return (
    <>
      <div className="flex w-full items-baseline gap-5">
        <span aria-hidden className="invisible text-lg font-semibold">
          {quantity}
        </span>

        <span className="text-sm text-text-secondary">{label}</span>
      </div>

      {depositDisplayPrice ? (
        <div className="flex w-full items-baseline justify-between gap-5">
          <div className="flex items-baseline gap-5">
            <span aria-hidden className="invisible text-lg font-semibold">
              {quantity}
            </span>

            <span className="pl-4 text-sm text-text-secondary">
              <T _str="+ Deposit Fee" />
            </span>
          </div>

          <span className="text-sm text-text-secondary">
            {depositDisplayPrice}
          </span>
        </div>
      ) : null}
    </>
  );
};
